import dayjs from 'dayjs';
import { departmentConfigs } from '../../pages/payroll/Deductions/config';

export const FINALIZED_DEDUCTION_STATUSES = ['completed', 'archived'];
export const DEDUCTION_DASHBOARD_OWNER_EMAIL = 'a.domacena@sparkles.com.ph';
export const PROCUREMENT_DEDUCTION_TITLE_SEPARATOR = ', ';
export const CUSTOM_DEDUCTION_TYPE_NAME_PATTERN = /^[A-Za-z0-9 ]+$/;

export const getEmployeeDisplayName = (employee = {}) => {
  const directName = [
    employee.displayName,
    employee.employeeName,
    employee.employeename,
    employee.name,
    employee.fullName,
    employee.fullname
  ].find((value) => typeof value === 'string' && value.trim());

  if (directName) {
    return directName.trim();
  }

  const orderedParts = [
    employee.lastName || employee.lastname,
    employee.firstName || employee.firstname,
    employee.middleName || employee.middlename
  ].filter(Boolean);

  if (orderedParts.length > 0) {
    return orderedParts.join(', ').trim();
  }

  const naturalParts = [
    employee.firstName || employee.firstname,
    employee.middleName || employee.middlename,
    employee.lastName || employee.lastname
  ].filter(Boolean);

  return naturalParts.join(' ').trim();
};

export const getEmployeeIdentifier = (employee = {}) => {
  const candidate = [
    employee.employeeId,
    employee.employeeid,
    employee.employee_id,
    employee.idNumber,
    employee.idnumber,
    employee.empId,
    employee.empid,
    employee.bioId,
    employee.bioid,
    employee._id,
    employee.id
  ].find((value) => value !== null && value !== undefined && String(value).trim());

  return candidate ? String(candidate).trim() : '';
};

export const mergeEmployeeLists = (...employeeGroups) => {
  const mergedEmployees = new Map();

  employeeGroups.flat().forEach((employee) => {
    const employeeId = getEmployeeIdentifier(employee);
    const employeeName = getEmployeeDisplayName(employee);

    if (!employeeId || !employeeName || mergedEmployees.has(employeeId)) {
      return;
    }

    mergedEmployees.set(employeeId, {
      employeeId,
      employeeName
    });
  });

  return [...mergedEmployees.values()].sort((left, right) => left.employeeName.localeCompare(right.employeeName));
};

export const normalizeDeductionTypeList = (types = []) => {
  const normalizedTypes = [];
  const seenValues = new Set();

  types.forEach((type) => {
    const trimmedType = String(type || '').trim();
    const normalizedKey = trimmedType.toLowerCase();

    if (!trimmedType || seenValues.has(normalizedKey)) {
      return;
    }

    seenValues.add(normalizedKey);
    normalizedTypes.push(trimmedType);
  });

  return normalizedTypes;
};

export const normalizeProcurementTypeSelection = (values = []) => {
  const nextSelections = [];
  const seenValues = new Set();

  values.forEach((value) => {
    const trimmedValue = String(value || '').trim();
    const normalizedKey = trimmedValue.toLowerCase();

    if (!trimmedValue || seenValues.has(normalizedKey)) {
      return;
    }

    seenValues.add(normalizedKey);
    nextSelections.push(trimmedValue);
  });

  return nextSelections;
};

export const splitProcurementDeductionTitle = (value = '') =>
  normalizeProcurementTypeSelection(String(value || '').split(','));

export const buildProcurementDeductionTitle = (values = []) =>
  normalizeProcurementTypeSelection(values).join(PROCUREMENT_DEDUCTION_TITLE_SEPARATOR);

export const isValidCustomDeductionTypeName = (value = '') =>
  CUSTOM_DEDUCTION_TYPE_NAME_PATTERN.test(String(value || '').trim());

export const normalizeDepartmentTypeOptions = (departmentKey, types = []) => {
  const normalizedTypes = normalizeDeductionTypeList(types);

  if (departmentKey !== 'procurement') {
    return normalizedTypes;
  }

  return normalizedTypes.filter((type) => !String(type || '').includes(','));
};

export const isDeductionDashboardOwner = (user) =>
  String(user?.email || '').trim().toLowerCase() === DEDUCTION_DASHBOARD_OWNER_EMAIL;

export const normalizeStoreKey = (value) => String(value || '').trim().toLowerCase();

export const getDefaultDepartmentTypeMap = () =>
  Object.fromEntries(
    Object.entries(departmentConfigs).map(([departmentKey, config]) => [
      departmentKey,
      normalizeDeductionTypeList(config.types)
    ])
  );

export const getEmptyCustomTypeMap = () => ({
  fgi: [],
  procurement: [],
  insurance: []
});

export const normalizeRecordDate = (value) => {
  if (!value) {
    return '';
  }

  const parsedValue = dayjs(value);
  return parsedValue.isValid() ? parsedValue.format('YYYY-MM-DD') : '';
};

export const normalizeAppliedPayrollRecord = (record = {}) => ({
  payrollId: record.payroll_id || record.payrollId || '',
  payrollInfoId: record.payroll_info_id || record.payrollInfoId || '',
  installmentNumber: Number(record.installment_number ?? record.installmentNumber ?? 0),
  amount: Number(record.amount || 0),
  payrollFrom: normalizeRecordDate(record.payroll_from || record.payrollFrom),
  payrollTo: normalizeRecordDate(record.payroll_to || record.payrollTo),
  cutoff: Number(record.cutoff || 0) || null,
  appliedAt: record.applied_at || record.appliedAt || '',
  generatedBy: record.generated_by_name || record.generatedBy || record.generated_by || '',
  approvedBy: record.approved_by || record.approvedBy || ''
});

export const normalizeApiDeductionRecord = (record = {}) => ({
  id: record._id || record.id || '',
  department: String(record.department || '').toLowerCase(),
  employeeName: record.employee_name || record.employeeName || '',
  employeeId: record.employee_id || record.employeeId || '',
  store: record.store || '',
  deductionType: record.deduction_type || record.deductionType || '',
  deductionTypeItems: Array.isArray(record.deduction_type_items || record.deductionTypeItems)
    ? (record.deduction_type_items || record.deductionTypeItems)
        .map((item) => String(item || '').trim())
        .filter(Boolean)
    : [],
  frequency: record.frequency || 'Per cutoff',
  amount: Number(record.amount || 0),
  totalAmount: Number(record.total_amount ?? record.totalAmount ?? 0),
  paidInstallments: Number(record.paid_installments ?? record.paidInstallments ?? 0),
  totalInstallments: Number(record.total_installments ?? record.totalInstallments ?? 1),
  firstDeductionDate: normalizeRecordDate(record.first_deduction_date || record.firstDeductionDate),
  lastDeductionDate: normalizeRecordDate(record.last_deduction_date || record.lastDeductionDate),
  status: record.status || 'active',
  archivedFromStatus: record.archived_from_status || record.archivedFromStatus || undefined,
  autoStop: Boolean(record.auto_stop ?? record.autoStop ?? true),
  remarks: record.remarks || '',
  appliedPayrolls: Array.isArray(record.applied_payrolls || record.appliedPayrolls)
    ? (record.applied_payrolls || record.appliedPayrolls).map(normalizeAppliedPayrollRecord)
    : []
});

export const getDeductionTypeOptionsForRecord = (record = {}) => {
  const departmentKey = String(record.department || '').toLowerCase() || 'general';

  if (departmentKey !== 'procurement') {
    return normalizeDepartmentTypeOptions(departmentKey, [record.deductionType]);
  }

  const procurementItems = normalizeDepartmentTypeOptions(
    departmentKey,
    Array.isArray(record.deductionTypeItems) ? record.deductionTypeItems : []
  );

  if (procurementItems.length > 0) {
    return procurementItems;
  }

  return normalizeDepartmentTypeOptions(departmentKey, [record.deductionType]);
};

export const buildDepartmentTypeMapFromRecords = (records = []) =>
  records.reduce((typeMap, record) => {
    const departmentKey = String(record.department || '').toLowerCase() || 'general';

    typeMap[departmentKey] = normalizeDepartmentTypeOptions(departmentKey, [
      ...(typeMap[departmentKey] || []),
      ...getDeductionTypeOptionsForRecord(record)
    ]);

    return typeMap;
  }, {});

export const mergeDepartmentTypeMaps = (...typeMaps) => {
  const defaultTypeMap = getDefaultDepartmentTypeMap();
  const departmentKeys = new Set(Object.keys(defaultTypeMap));

  typeMaps.forEach((typeMap) => {
    Object.keys(typeMap || {}).forEach((departmentKey) => {
      departmentKeys.add(departmentKey);
    });
  });

  return Object.fromEntries(
    [...departmentKeys].map((departmentKey) => [
      departmentKey,
      (() => {
        const mergedTypes = normalizeDepartmentTypeOptions(
          departmentKey,
          typeMaps.flatMap((typeMap) => typeMap?.[departmentKey] || [])
        );

        if (mergedTypes.length > 0) {
          return mergedTypes;
        }

        return normalizeDepartmentTypeOptions(departmentKey, defaultTypeMap[departmentKey] || []);
      })()
    ])
  );
};

export const normalizeCustomTypesMeta = (rawCustomTypes = {}) =>
  Object.fromEntries(
    Object.entries(rawCustomTypes).map(([departmentKey, items]) => [
      departmentKey,
      (items || []).map((item) => ({
        id: item.id || item._id,
        name: item.name,
        price: Number(item.price || 0)
      }))
    ])
  );
